import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	FlexGap,
	Heading3,
	Paragraph,
	PlaceholderButton,
	QuillText,
	tokens,
} from '@treams/treams-ui';

import GoalCard from './GoalCard';

const Status = styled(Paragraph)`
	color: ${tokens.brand.text};
`;

function GoalMoment(props: { userID: string; instance: graphql.MomentInstance }) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const required = props.instance.momentTemplate.goals?.minimumGoals ?? 1;
	const count = props.instance.goals?.length ?? 0;
	const goals = props.instance.goals ?? [];

	return (
		<Box mb={`${tokens.gap.section}px`}>
			<Flex mb={`${tokens.gap.list}px`}>
				<Box flexGrow={1} flexShrink={1}>
					<Heading3 mb={`${tokens.gap.inline}px`}>
						{localize(props.instance.momentTemplate.title).text}
					</Heading3>
					<Paragraph>
						<QuillText value={localize(props.instance.momentTemplate.description ?? [])?.text} />
					</Paragraph>
				</Box>
				{(props.instance.momentTemplate.goals?.minimumGoals ?? 0) > 0 && (
					<Box ml={2} flexGrow={0} flexShrink={0}>
						<Status>
							{count}/{required} {t('goals.moments.count')}
						</Status>
					</Box>
				)}
			</Flex>
			{goals.length > 0 && (
				<FlexGap flexDirection="column" gap={tokens.gap.list} mb={`${tokens.gap.list}px`}>
					{goals.map((goal) => (
						<GoalCard key={goal.id} goal={goal as graphql.Goal} owner={false} />
					))}
				</FlexGap>
			)}
			<PlaceholderButton
				title={t('goals.moments.connect')}
				to={`/users/${props.userID}/goals/new?moment_id=${props.instance.id}`}
			/>
		</Box>
	);
}

export default function GoalMoments(props: { userID: string }) {
	const query = graphql.useGoalMomentsQuery({
		variables: { id: props.userID },
		fetchPolicy: 'network-only',
	});

	return (
		<Box>
			{query.data?.user.moments
				.filter((instance) => instance.status === graphql.MomentStatus.Open)
				.filter((instance) => moment(instance.endDate).isAfter(moment().subtract(2, 'months')))
				.filter((instance) => instance.momentTemplate.type === graphql.MomentType.Goals)
				.map((instance) => (
					<GoalMoment
						key={instance.id}
						userID={props.userID}
						instance={instance as graphql.MomentInstance}
					/>
				))}
		</Box>
	);
}
